"use client";
import React from "react";
import getCourseRunEnrollButtonText from "@/lib/course/utils/getCourseRunEnrollButtonText";
import { cn } from "@/lib/utils";
import { CourseEnrollButtonProps } from "./types";
import { Alert } from "@/components/ui/alert";
import AuditLink from "../ContentRefresh/CourseEnroll/CourseEnrollButtons/AuditLink";
import EnrollLink from "../ContentRefresh/CourseEnroll/CourseEnrollButtons/EnrollLink";
import useCourseEnrollment from "../ContentRefresh/CourseEnroll/hooks/useCourseEnrollment";
import useTranslations from "@/i18n/useTranslations";

function CourseEnrollButton({
  buttonClass = "",
  className = "",
  courseRun,
  course,
  isSelfHiding,
  showAuditLink = true,
}: CourseEnrollButtonProps) {
  const t = useTranslations("Course");
  const {
    isLoggedIn,
    isUserEnrolledInRun,
    isCourseEnrollable,
    isCourseArchived,
    paidLeadValues,
  } = useCourseEnrollment({
    courseRun,
    course,
  });

  const { auditCourseLabel, enrollCtaLabel } = paidLeadValues ?? {};

  const enrollButtonMessage = getCourseRunEnrollButtonText(
    {
      enrollmentStart: courseRun.enrollmentStart,
      enrollmentEnd: courseRun.enrollmentEnd,
      end: courseRun.end,
    },
    isUserEnrolledInRun || false,
  );

  const enrollButtonText = t(
    enrollButtonMessage.key,
    enrollButtonMessage.values,
  );

  const ctaLabel =
    enrollButtonMessage.key === "enroll" && enrollCtaLabel
      ? enrollCtaLabel
      : undefined;

  const hideButton =
    isSelfHiding &&
    (!isCourseEnrollable || isCourseArchived || isUserEnrolledInRun);

  if (hideButton) {
    return null;
  }

  const isStickyButtonTest = isSelfHiding && ctaLabel;

  const auditCourse =
    !showAuditLink || isCourseArchived ? null : (
      <AuditLink
        variant="anchor"
        courseRun={courseRun}
        course={course}
        className={`${!isStickyButtonTest ? "mt-4" : "px-0"}`}
      />
    );

  return (
    <div className={className}>
      {!isLoggedIn && isCourseEnrollable && (
        <div
          className={cn(
            "flex",
            isStickyButtonTest ? "flex-row gap-3 items-center" : "flex-col",
          )}
        >
          {isStickyButtonTest ? (
            <span className="font-bold">{t("startToday")}</span>
          ) : null}
          <EnrollLink
            buttonClass={cn(
              buttonClass,
              isStickyButtonTest && "rounded-full px-6",
            )}
            courseRun={courseRun}
            course={course}
          />
          {auditCourseLabel ? auditCourse : null}
        </div>
      )}
      {isLoggedIn && !isUserEnrolledInRun && isCourseEnrollable && (
        <div
          className={cn(
            "flex",
            isStickyButtonTest ? "flex-row gap-3 items-center" : "flex-col",
          )}
        >
          {isStickyButtonTest ? (
            <span className="font-bold">{t("startToday")}</span>
          ) : null}
          <EnrollLink
            buttonClass={cn(
              isUserEnrolledInRun
                ? "bg-white border text-brand hover:bg-brand hover:text-white"
                : "",
              buttonClass,
              isStickyButtonTest && "rounded-full px-6",
            )}
            courseRun={courseRun}
            course={course}
          />
          {auditCourseLabel ? auditCourse : null}
        </div>
      )}
      {isLoggedIn && isUserEnrolledInRun && (
        <EnrollLink
          buttonClass={buttonClass}
          courseRun={courseRun}
          course={course}
        />
      )}

      {!isUserEnrolledInRun && !isCourseEnrollable && (
        <Alert className="p-6 rounded-xl bg-putty-100 text-primary drop-shadow-md text-center text-base">
          {enrollButtonText}
        </Alert>
      )}
      {!isStickyButtonTest && !auditCourseLabel ? auditCourse : null}
    </div>
  );
}

export default CourseEnrollButton;
